import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

interface ApiEndpointCardProps {
  path: string;
  method: string;
  summary: string;
  description?: string;
  parameters?: any[];
  requestBody?: any;
  responses?: any;
}

const ApiEndpointCard: React.FC<ApiEndpointCardProps> = ({
  path,
  method,
  summary,
  description,
  parameters,
  requestBody,
  responses
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const methodColors: Record<string, string> = {
    get: 'bg-green-100 text-green-800 border-green-200',
    post: 'bg-blue-100 text-blue-800 border-blue-200',
    put: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    delete: 'bg-red-100 text-red-800 border-red-200',
    patch: 'bg-purple-100 text-purple-800 border-purple-200',
    options: 'bg-gray-100 text-gray-800 border-gray-200',
    head: 'bg-gray-100 text-gray-800 border-gray-200'
  };

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden mb-4 bg-white shadow-sm transition-all duration-200 hover:shadow-md">
      <div 
        className="flex items-center justify-between p-4 cursor-pointer"
        onClick={toggleExpanded}
      >
        <div className="flex items-center space-x-3">
          <div className={`uppercase text-xs font-bold py-1 px-2 rounded border ${methodColors[method.toLowerCase()] || 'bg-gray-100 text-gray-800 border-gray-200'}`}>
            {method}
          </div>
          <div className="font-mono text-sm">{path}</div>
        </div>
        <div className="flex items-center">
          <div className="text-sm text-gray-600 mr-3">{summary}</div>
          {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </div>
      </div>

      {isExpanded && (
        <div className="border-t border-gray-200 p-4 bg-gray-50">
          {description && (
            <div className="mb-4">
              <h4 className="text-sm font-semibold text-gray-700 mb-1">Description</h4>
              <p className="text-sm text-gray-600">{description}</p>
            </div>
          )}

          {parameters && parameters.length > 0 && (
            <div className="mb-4">
              <h4 className="text-sm font-semibold text-gray-700 mb-2">Parameters</h4>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Name</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">In</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Required</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    {parameters.map((param, index) => (
                      <tr key={index} className="border-t border-gray-200">
                        <td className="px-4 py-2 font-mono text-xs">{param.name}</td>
                        <td className="px-4 py-2">{param.in}</td>
                        <td className="px-4 py-2">
                          {param.required ? (
                            <span className="bg-red-100 text-red-800 text-xs py-0.5 px-2 rounded">Required</span>
                          ) : (
                            <span className="bg-gray-100 text-gray-800 text-xs py-0.5 px-2 rounded">Optional</span>
                          )}
                        </td>
                        <td className="px-4 py-2 text-gray-600">{param.description || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {requestBody && (
            <div className="mb-4">
              <h4 className="text-sm font-semibold text-gray-700 mb-2">Request Body</h4>
              <div className="bg-gray-100 p-3 rounded-md">
                <div className="mb-2 text-xs text-gray-600">
                  {Object.keys(requestBody.content || {}).map(contentType => (
                    <div key={contentType} className="mb-1">
                      <span className="font-mono">{contentType}</span>
                    </div>
                  ))}
                </div>
                {requestBody.content?.['application/json']?.schema?.example && (
                  <pre className="text-xs bg-gray-800 text-gray-100 p-3 rounded overflow-x-auto">
                    {JSON.stringify(requestBody.content['application/json'].schema.example, null, 2)}
                  </pre>
                )}
              </div>
            </div>
          )}

          {responses && Object.keys(responses).length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-gray-700 mb-2">Responses</h4>
              <div className="space-y-3">
                {Object.entries(responses).map(([statusCode, response]: [string, any]) => (
                  <div key={statusCode} className="bg-gray-100 p-3 rounded-md">
                    <div className="flex items-center mb-2">
                      <div className={`text-xs py-0.5 px-2 rounded ${
                        statusCode.startsWith('2') ? 'bg-green-100 text-green-800' : 
                        statusCode.startsWith('4') ? 'bg-yellow-100 text-yellow-800' : 
                        statusCode.startsWith('5') ? 'bg-red-100 text-red-800' : 
                        'bg-blue-100 text-blue-800'
                      }`}>
                        {statusCode}
                      </div>
                      <div className="ml-2 text-sm text-gray-600">{response.description}</div>
                    </div>
                    {response.content?.['application/json']?.schema?.example && (
                      <pre className="text-xs bg-gray-800 text-gray-100 p-3 rounded overflow-x-auto">
                        {JSON.stringify(response.content['application/json'].schema.example, null, 2)}
                      </pre>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ApiEndpointCard;